import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/hooks/use-auth'
import { useLang } from '@/hooks/use-lang'
import { ApiError } from '@/lib/api'
import { t } from '@/lib/i18n'
import { trackSignupConversion } from '@/lib/analytics'
import { Wordmark } from '@/components/wordmark'

export function SignupPage() {
  useLang()
  const { signup } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const inviteCode = searchParams.get('invite') ?? undefined
  // A /tmp inbox slug: signing up from a live tester imports its events.
  const claim = searchParams.get('claim') ?? undefined
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [agreed, setAgreed] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const redirectUrl = await signup(email, password, name, undefined, inviteCode, claim)
      trackSignupConversion() // Google Ads conversion (no-op unless VITE_GADS_ID set)
      if (redirectUrl) {
        window.location.href = redirectUrl
      } else {
        navigate('/')
      }
    } catch (err) {
      // Map specific backend errors to clear, user-actionable messages.
      // 409 = email already registered (most common); fall back to generic
      // for transient / unexpected failures.
      if (err instanceof ApiError && err.status === 409) {
        // The email already has an account. If they arrived via an invite,
        // route them to login carrying the invite so they can sign in and
        // accept it instead of hitting a dead end.
        if (inviteCode) { navigate(`/login?invite=${encodeURIComponent(inviteCode)}`); return }
        setError(t('emailAlreadyTaken'))
      } else if (err instanceof ApiError && err.code === 'disposable_email') {
        setError(t('disposableEmailBlocked'))
      } else if (err instanceof ApiError && err.status === 422) {
        setError(t('signupValidationError'))
      } else {
        setError(t('signupFailed'))
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg)' }}>
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <img src="/logo.png" className="w-12 h-12 mx-auto mb-4" alt="WebhookLens" />
          <h1 className="text-xl font-semibold" style={{ color: 'var(--white)' }}><Wordmark /></h1>
          <p className="text-sm mt-1" style={{ color: 'var(--muted)' }}>{t('signUpTitle')}</p>
        </div>
        {inviteCode && (
          <p className="text-sm p-3 rounded-lg text-center" style={{ color: 'var(--accent-ink, var(--accent))', backgroundColor: 'var(--accent-soft)', border: '1px solid color-mix(in srgb, var(--accent) 25%, transparent)' }}>
            {t('joiningWorkspace')}
          </p>
        )}
        <form onSubmit={handleSubmit} className="space-y-4 p-6 rounded-xl" style={{ backgroundColor: 'var(--surface)', border: '1px solid var(--border)' }}>
          <a
            href={claim ? `/api/auth/github?claim=${encodeURIComponent(claim)}` : '/api/auth/github'}
            className="flex items-center justify-center gap-2.5 w-full py-2.5 rounded-lg text-sm font-medium transition"
            style={{ backgroundColor: '#21262d', color: '#e6edf3', border: '1px solid #30363d' }}
            onMouseEnter={e => (e.currentTarget as HTMLElement).style.backgroundColor = '#30363d'}
            onMouseLeave={e => (e.currentTarget as HTMLElement).style.backgroundColor = '#21262d'}
          >
            <svg className="w-5 h-5" viewBox="0 0 98 96" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" clipRule="evenodd" d="M48.854 0C21.839 0 0 22 0 49.217c0 21.756 13.993 40.172 33.405 46.69 2.427.49 3.316-1.059 3.316-2.362 0-1.141-.08-5.052-.08-9.127-13.59 2.934-16.42-5.867-16.42-5.867-2.184-5.704-5.42-7.17-5.42-7.17-4.448-3.015.324-3.015.324-3.015 4.934.326 7.523 5.052 7.523 5.052 4.367 7.496 11.404 5.378 14.235 4.074.404-3.178 1.699-5.378 3.074-6.6-10.839-1.141-22.243-5.378-22.243-24.283 0-5.378 1.94-9.778 5.014-13.2-.485-1.222-2.184-6.275.486-13.038 0 0 4.125-1.304 13.426 5.052a46.97 46.97 0 0 1 12.214-1.63c4.125 0 8.33.571 12.213 1.63 9.302-6.356 13.427-5.052 13.427-5.052 2.67 6.763.97 11.816.485 13.038 3.155 3.422 5.015 7.822 5.015 13.2 0 18.905-11.404 23.06-22.324 24.283 1.78 1.548 3.316 4.481 3.316 9.126 0 6.6-.08 11.897-.08 13.526 0 1.304.89 2.853 3.316 2.364 19.412-6.52 33.405-24.935 33.405-46.691C97.707 22 75.788 0 48.854 0z" />
            </svg>
            {t('sign_in_with_github')}
          </a>
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px" style={{ backgroundColor: 'var(--border)' }} />
            <span className="text-xs" style={{ color: 'var(--muted)' }}>{t('or')}</span>
            <div className="flex-1 h-px" style={{ backgroundColor: 'var(--border)' }} />
          </div>
          {error && (
            <p className="text-sm p-3 rounded-lg" style={{ color: 'var(--red)', backgroundColor: 'color-mix(in srgb, var(--red) 12%, transparent)', border: '1px solid color-mix(in srgb, var(--red) 25%, transparent)' }}>
              {error}
            </p>
          )}
          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--muted)' }}>{t('name')}</label>
            <input type="text" required value={name} onChange={e => setName(e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none transition"
              style={{ backgroundColor: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }}
              onFocus={e => (e.target as HTMLInputElement).style.borderColor = 'var(--accent)'}
              onBlur={e => (e.target as HTMLInputElement).style.borderColor = 'var(--border)'}
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--muted)' }}>{t('email')}</label>
            <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none transition"
              style={{ backgroundColor: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }}
              onFocus={e => (e.target as HTMLInputElement).style.borderColor = 'var(--accent)'}
              onBlur={e => (e.target as HTMLInputElement).style.borderColor = 'var(--border)'}
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--muted)' }}>{t('password')}</label>
            <input type="password" required minLength={8} value={password} onChange={e => setPassword(e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none transition"
              style={{ backgroundColor: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }}
              onFocus={e => (e.target as HTMLInputElement).style.borderColor = 'var(--accent)'}
              onBlur={e => (e.target as HTMLInputElement).style.borderColor = 'var(--border)'}
            />
          </div>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={agreed}
              onChange={e => setAgreed(e.target.checked)}
              className="mt-0.5 rounded"
              style={{ accentColor: 'var(--accent)' }}
            />
            <span className="text-xs" style={{ color: 'var(--muted)' }}>
              {t('agreeToTerms')}{' '}
              <a href="/terms" target="_blank" style={{ color: 'var(--accent)', textDecoration: 'none' }}>{t('termsOfService')}</a>
              {' '}{t('and')}{' '}
              <a href="/privacy" target="_blank" style={{ color: 'var(--accent)', textDecoration: 'none' }}>{t('privacyPolicy')}</a>
            </span>
          </label>
          <button type="submit" disabled={loading || !agreed}
            className="btn-accent w-full py-2.5 rounded-lg text-sm font-medium"
          >
            {loading ? t('creatingAccount') : t('signUp')}
          </button>
          <p className="text-sm text-center" style={{ color: 'var(--muted)' }}>
            {t('hasAccount')}{' '}
            <Link to="/login" className="font-medium transition" style={{ color: 'var(--accent)' }}
              onMouseEnter={e => (e.currentTarget as HTMLElement).style.color = 'var(--accent-bright)'}
              onMouseLeave={e => (e.currentTarget as HTMLElement).style.color = 'var(--accent)'}
            >
              {t('signIn')}
            </Link>
          </p>
        </form>
      </div>
    </div>
  )
}
